/**
 * Connection Form Component
 * Form for creating and editing API connections
 */

import { useState, useEffect } from 'react';
import { Connection } from '../../types';
import { Eye, EyeOff } from 'lucide-react';

// Extended connection type for form (includes auth credentials)
interface ConnectionFormData {
  name: string;
  description: string;
  base_url: string;
  tenant: string;
  auth_type: 'bearer' | 'oauth2' | 'basic';
  // Bearer auth
  bearer_token?: string;
  // OAuth2 auth
  oauth2_token_url?: string;
  oauth2_client_id?: string;
  oauth2_client_secret?: string;
  oauth2_scope?: string;
  // Service Account (for Infor ION API password grant)
  service_account_key?: string;
  service_account_secret?: string;
  // Basic auth
  basic_username?: string;
  basic_password?: string;
  // Custom headers
  custom_headers?: Record<string, string>;
}

interface ConnectionFormProps {
  connection?: Connection | null;
  onSubmit: (data: Partial<ConnectionFormData>) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
}

export function ConnectionForm({ connection, onSubmit, onCancel, isSubmitting }: ConnectionFormProps) {
  const [showSecrets, setShowSecrets] = useState(false);
  const [formData, setFormData] = useState<ConnectionFormData>({
    name: '',
    description: '',
    base_url: '',
    tenant: '',
    auth_type: 'oauth2',
    oauth2_token_url: '',
    oauth2_client_id: '',
    oauth2_client_secret: '',
    oauth2_scope: '',
    service_account_key: '',
    service_account_secret: '',
    bearer_token: '',
    basic_username: '',
    basic_password: '',
    custom_headers: {},
  });

  const [headerKey, setHeaderKey] = useState('');
  const [headerValue, setHeaderValue] = useState('');
  const [jsonHeaders, setJsonHeaders] = useState('');
  const [jsonError, setJsonError] = useState('');

  useEffect(() => {
    if (connection) {
      setFormData({
        name: connection.name || '',
        description: connection.description || '',
        base_url: connection.base_url || '',
        tenant: connection.tenant || '',
        auth_type: connection.auth_type || 'oauth2',
        // OAuth2 fields - load from connection (secrets will be empty, user must re-enter if changing)
        oauth2_token_url: connection.token_url || '',
        oauth2_client_id: connection.client_id || '',
        oauth2_client_secret: '', // Secret is not returned from API for security
        oauth2_scope: connection.oauth2_scope || '',
        // Service Account - secrets not returned
        service_account_key: '',
        service_account_secret: '',
        // Bearer/Basic - secrets not returned
        bearer_token: '',
        basic_username: '',
        basic_password: '',
        // Custom headers
        custom_headers: connection.custom_headers || {},
      });
    }
  }, [connection]);

  const handleAddHeader = () => {
    if (headerKey && headerValue) {
      setFormData({
        ...formData,
        custom_headers: {
          ...formData.custom_headers,
          [headerKey]: headerValue,
        },
      });
      setHeaderKey('');
      setHeaderValue('');
    }
  };

  const handleRemoveHeader = (key: string) => {
    const { [key]: _, ...rest } = formData.custom_headers || {};
    setFormData({ ...formData, custom_headers: rest });
  };

  const handleImportJsonHeaders = () => {
    setJsonError('');
    if (!jsonHeaders.trim()) {
      setJsonError('Please enter JSON');
      return;
    }
    try {
      const parsed = JSON.parse(jsonHeaders);
      if (typeof parsed !== 'object' || Array.isArray(parsed)) {
        setJsonError('JSON must be an object (e.g. {"key": "value"})');
        return;
      }
      // Validate all values are strings
      const validHeaders: Record<string, string> = {};
      for (const [key, value] of Object.entries(parsed)) {
        if (typeof value !== 'string') {
          validHeaders[key] = String(value);
        } else {
          validHeaders[key] = value;
        }
      }
      setFormData({
        ...formData,
        custom_headers: {
          ...formData.custom_headers,
          ...validHeaders,
        },
      });
      setJsonHeaders('');
    } catch {
      setJsonError('Invalid JSON format');
    }
  };

  const handleClearAllHeaders = () => {
    setFormData({ ...formData, custom_headers: {} });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Name *
        </label>
        <input
          type="text"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="e.g. Infor LN Cloud"
          required
        />
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          rows={2}
          placeholder="Optional description"
        />
      </div>

      {/* Base URL */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Base URL *
        </label>
        <input
          type="url"
          value={formData.base_url}
          onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="https://mingle-ionapi.eu1.inforcloudsuite.com"
          required
        />
      </div>

      {/* Tenant */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Tenant
        </label>
        <input
          type="text"
          value={formData.tenant}
          onChange={(e) => setFormData({ ...formData, tenant: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
          placeholder="e.g. YJ8UENUT9D2RNYGA_DEM"
        />
      </div>

      {/* Auth Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Authentication *
        </label>
        <select
          value={formData.auth_type}
          onChange={(e) => setFormData({ ...formData, auth_type: e.target.value as 'bearer' | 'oauth2' | 'basic' })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
        >
          <option value="oauth2">OAuth2 Client Credentials</option>
          <option value="bearer">Bearer Token</option>
          <option value="basic">Basic Authentication</option>
        </select>
      </div>

      {/* OAuth2 Fields */}
      {formData.auth_type === 'oauth2' && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900">OAuth2 Configuration</h4>

          {/* Show hint if editing existing connection with credentials */}
          {connection?.has_credentials && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <strong>Note:</strong> This connection already has saved credentials.
              Client Secret is not displayed for security reasons.
              Leave the field empty to keep the existing credentials.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token URL *
            </label>
            <input
              type="url"
              value={formData.oauth2_token_url}
              onChange={(e) => setFormData({ ...formData, oauth2_token_url: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
              placeholder="https://sso.example.com/as/token.oauth2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client ID *
            </label>
            <input
              type="text"
              value={formData.oauth2_client_id}
              onChange={(e) => setFormData({ ...formData, oauth2_client_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
              placeholder="Client ID"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Secret *
            </label>
            <div className="relative">
              <input
                type={showSecrets ? 'text' : 'password'}
                value={formData.oauth2_client_secret}
                onChange={(e) => setFormData({ ...formData, oauth2_client_secret: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                placeholder="Client Secret"
              />
              <button
                type="button"
                onClick={() => setShowSecrets(!showSecrets)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scope
            </label>
            <input
              type="text"
              value={formData.oauth2_scope}
              onChange={(e) => setFormData({ ...formData, oauth2_scope: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
              placeholder="e.g. Infor-LN"
            />
          </div>

          {/* Service Account Section */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            <h5 className="font-medium text-gray-800 text-sm mb-2">Service Account Credentials</h5>
            <p className="text-xs text-gray-500 mb-3">
              For Infor ION API, a Service Account is required (Resource Owner Password Flow).
            </p>

            {/* Show hint if editing existing connection with service account */}
            {connection?.has_service_account && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700 mb-3">
                <strong>Note:</strong> Service Account is configured.
                Leave the fields empty to keep the existing credentials.
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Account Key
              </label>
              <div className="relative">
                <input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.service_account_key}
                  onChange={(e) => setFormData({ ...formData, service_account_key: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                  placeholder="Service Account Access Key"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Service Account Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets ? 'text' : 'password'}
                  value={formData.service_account_secret}
                  onChange={(e) => setFormData({ ...formData, service_account_secret: e.target.value })}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                  placeholder="Service Account Secret Key"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets(!showSecrets)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bearer Token Fields */}
      {formData.auth_type === 'bearer' && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900">Bearer Token</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token *
            </label>
            <div className="relative">
              <input
                type={showSecrets ? 'text' : 'password'}
                value={formData.bearer_token}
                onChange={(e) => setFormData({ ...formData, bearer_token: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                placeholder="Bearer Token"
              />
              <button
                type="button"
                onClick={() => setShowSecrets(!showSecrets)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Basic Auth Fields */}
      {formData.auth_type === 'basic' && (
        <div className="border border-gray-200 rounded-lg p-4 space-y-3">
          <h4 className="font-medium text-gray-900">Basic Authentication</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Username *
            </label>
            <input
              type="text"
              value={formData.basic_username}
              onChange={(e) => setFormData({ ...formData, basic_username: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
              placeholder="Username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Password *
            </label>
            <div className="relative">
              <input
                type={showSecrets ? 'text' : 'password'}
                value={formData.basic_password}
                onChange={(e) => setFormData({ ...formData, basic_password: e.target.value })}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500"
                placeholder="Password"
              />
              <button
                type="button"
                onClick={() => setShowSecrets(!showSecrets)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showSecrets ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Headers */}
      <div className="border border-gray-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-medium text-gray-900">Custom Headers</h4>
          {Object.keys(formData.custom_headers || {}).length > 0 && (
            <button
              type="button"
              onClick={handleClearAllHeaders}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Remove all
            </button>
          )}
        </div>

        {/* Existing Headers */}
        {Object.entries(formData.custom_headers || {}).map(([key, value]) => (
          <div key={key} className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded">
            <span className="font-mono text-sm font-medium">{key}:</span>
            <span className="font-mono text-sm text-gray-600 flex-1 truncate" title={value}>{value}</span>
            <button
              type="button"
              onClick={() => handleRemoveHeader(key)}
              className="text-red-500 hover:text-red-700 text-sm"
            >
              Remove
            </button>
          </div>
        ))}

        {/* Add New Header */}
        <div className="flex gap-2">
          <input
            type="text"
            value={headerKey}
            onChange={(e) => setHeaderKey(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500 text-sm"
            placeholder="Header Name (e.g. X-Infor-LnCompany)"
          />
          <input
            type="text"
            value={headerValue}
            onChange={(e) => setHeaderValue(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500 text-sm"
            placeholder="Header Value"
          />
          <button
            type="button"
            onClick={handleAddHeader}
            className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
          >
            Add
          </button>
        </div>

        {/* JSON Import */}
        <div className="border-t border-gray-200 pt-3 mt-3">
          <p className="text-xs text-gray-500 mb-2">
            Or import multiple headers as JSON:
          </p>
          <textarea
            value={jsonHeaders}
            onChange={(e) => { setJsonHeaders(e.target.value); setJsonError(''); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#45bdd6] focus:border-blue-500 text-sm font-mono"
            rows={3}
            placeholder='{"Accept": "application/json", "X-Infor-LnCompany": "2100"}'
          />
          {jsonError && (
            <p className="text-xs text-red-500 mt-1">{jsonError}</p>
          )}
          <button
            type="button"
            onClick={handleImportJsonHeaders}
            className="mt-2 px-3 py-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
          >
            Import JSON
          </button>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-[#45bdd6] text-white rounded-lg hover:bg-[#3aa8bf] transition-colors disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : connection ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}
